import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inputs, DEFAULT_INPUTS, IndustryType, INDUSTRY_DEFAULTS } from '../utils/roiCalculations';
import { fmtMoney } from '../utils/formatters';
type QuestionId = 'industry' | 'profitPerCustomer' | 'rookieSales' | 'vetSales' | 'closeRate';
interface SliderQuestion {
  id: QuestionId;
  question: string;
  subtitle?: string;
  subtitleJSX?: React.ReactNode;
  field: keyof Inputs;
  type: 'range';
  min?: number;
  max?: number;
  step?: number;
  formatValue?: (value: number) => string;
}
interface SelectQuestion {
  id: QuestionId;
  question: string;
  subtitle?: string;
  type: 'select';
  field: keyof Inputs;
  options: string[];
}
type Question = SliderQuestion | SelectQuestion;
const industryOptions = ['Windows', 'Roofing', 'Solar (Sales)', 'Solar (Vertical)', 'Solar (Community)', 'Pest', 'Fiber', 'Deregulated Energy', 'Hardscaping', 'Landscaping', 'Other'];
const questions: Question[] = [{
  id: 'industry',
  question: 'What industry are you in?',
  subtitle: 'This helps us provide industry-specific defaults',
  type: 'select',
  field: 'industry',
  options: industryOptions
}, {
  id: 'profitPerCustomer',
  question: 'What is your profit per customer?',
  subtitle: 'Gross profit per sale after commissions but before G&A / overhead',
  field: 'profitPerCustomer',
  type: 'range',
  min: 100,
  max: 50000,
  step: 100,
  formatValue: v => fmtMoney(v)
}, {
  id: 'rookieSales',
  question: 'How many sales do you expect from a new rep who has made it through their first 90 days?',
  subtitle: "Total sales from a new rep (who doesn't quit) in their first three months.",
  field: 'salesFirst90Total',
  type: 'range',
  min: 5,
  max: 25,
  step: 1,
  formatValue: v => `${v} sales`
}, {
  id: 'vetSales',
  question: 'For a new sales rep who MAKES IT A FULL YEAR, how many sales per month would you expect from them after their initial 90 day learning curve?',
  subtitle: 'Monthly sales for experienced reps',
  field: 'salesVetMonthly',
  type: 'range',
  min: 3,
  max: 20,
  step: 1,
  formatValue: v => `${v} sales/month`
}, {
  id: 'closeRate',
  question: 'What percentage of sales (signed contracts) do you expect to get installed?',
  subtitle: 'Your typical installation rate',
  field: 'pullthrough',
  type: 'range',
  min: 40,
  max: 100,
  step: 5,
  formatValue: v => `${v}%`
}];
export function CalculatorFlow() {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState<Inputs>({
    ...DEFAULT_INPUTS,
    industry: 'Roofing',
    firstName: '',
    lastName: '',
    phone: ''
  });
  const handleSliderChange = (field: keyof Inputs, value: number) => {
    const newInputs = {
      ...inputs,
      [field]: value
    };
    setInputs(newInputs);
    localStorage.setItem('calculatorInputs', JSON.stringify(newInputs));
  };
  const handleIndustryChange = (newIndustry: IndustryType) => {
    const defaults = INDUSTRY_DEFAULTS[newIndustry];
    const newInputs = {
      ...inputs,
      industry: newIndustry,
      profitPerCustomer: defaults?.profitPerCustomer || inputs.profitPerCustomer,
      salesFirst90Total: defaults?.salesFirst90Total || inputs.salesFirst90Total,
      salesVetMonthly: defaults?.salesVetMonthly || inputs.salesVetMonthly
    };
    setInputs(newInputs);
    localStorage.setItem('calculatorInputs', JSON.stringify(newInputs));
  };
  const handleContinue = () => {
    localStorage.setItem('calculatorInputs', JSON.stringify(inputs));
    navigate('/retention');
  };
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white" data-id="element-604">
      {/* Header */}
      <header className="border-b-2 border-gray-200 bg-white" data-id="element-605">
        <div className="max-w-[1400px] mx-auto px-4 py-3 md:py-6 flex items-center justify-between" data-id="element-606">
          <img src="https://cdn.magicpatterns.com/uploads/jrZpZA6KS7cefrnJNRcqNs/Copy_of_HIRE.png" alt="HIRE" className="h-8 md:h-12 cursor-pointer" onClick={() => navigate('/')} data-id="element-607" />
          <div className="px-3 py-1 bg-amber/10 border border-amber/30 rounded-full text-amber-700 text-xs md:text-sm font-semibold" data-id="element-608">
            YEAR-ROUND PROGRAM
          </div>
        </div>
      </header>

      <div className="max-w-[1000px] mx-auto px-4 py-8 md:py-12" data-id="element-609">
        {/* Hero */}
        <div className="text-center mb-8 md:mb-12" data-id="element-610">
          <h1 className="text-3xl md:text-5xl font-bold text-navy mb-3 md:mb-4" data-id="element-611">
            Tell Us About Your Business
          </h1>
          <p className="text-base md:text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-612">
            Answer these 5 quick questions to calculate your recruiting ROI
          </p>
        </div>

        {/* Questions Container */}
        <div className="space-y-6" data-id="element-613">
          {questions.map((question, index) => <div key={question.id} className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-6 md:p-8" data-id="element-614">
              <div className="mb-6" data-id="element-615">
                <div className="flex items-start gap-3 mb-4" data-id="element-616">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-navy text-white flex items-center justify-center font-bold text-sm" data-id="element-617">
                    {index + 1}
                  </div>
                  <div className="flex-1" data-id="element-618">
                    <h2 className="text-lg md:text-xl font-bold text-navy mb-2" data-id="element-619">
                      {question.question}
                    </h2>
                    {question.type === 'range' && (question as SliderQuestion).subtitleJSX ? <p className="text-sm text-gray-600" data-id="element-620">
                        {(question as SliderQuestion).subtitleJSX}
                      </p> : question.subtitle ? <p className="text-sm text-gray-600" data-id="element-621">
                        {question.subtitle}
                      </p> : null}
                  </div>
                </div>
              </div>

              {/* Select Input for Industry */}
              {question.type === 'select' && <div data-id="element-622">
                  <select value={inputs[question.field] as string} onChange={e => handleIndustryChange(e.target.value as IndustryType)} className="w-full p-4 rounded-lg border-2 border-gray-200 bg-white text-gray-900 text-lg font-semibold outline-none focus:border-amber transition-colors cursor-pointer" data-id="element-623">
                    <option value="" data-id="element-624">Select your industry</option>
                    {question.options.map(option => <option key={option} value={option} data-id="element-625">
                        {option}
                      </option>)}
                  </select>
                </div>}

              {/* Slider Input for Range Questions */}
              {question.type === 'range' && <div data-id="element-626">
                  <div className="flex items-center justify-between mb-3" data-id="element-627">
                    <span className="text-sm font-semibold text-gray-700" data-id="element-628">
                      Your Answer:
                    </span>
                    <span className="text-2xl md:text-3xl font-bold text-navy" data-id="element-629">
                      {(question as SliderQuestion).formatValue ? (question as SliderQuestion).formatValue!(inputs[(question as SliderQuestion).field] as number) : inputs[(question as SliderQuestion).field]}
                    </span>
                  </div>
                  <input type="range" min={(question as SliderQuestion).min} max={(question as SliderQuestion).max} step={(question as SliderQuestion).step} value={inputs[(question as SliderQuestion).field] as number} onChange={e => handleSliderChange((question as SliderQuestion).field, parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber" style={{
              background: `linear-gradient(to right, #F59E0B 0%, #F59E0B ${((inputs[(question as SliderQuestion).field] as number) - ((question as SliderQuestion).min || 0)) / (((question as SliderQuestion).max || 100) - ((question as SliderQuestion).min || 0)) * 100}%, #E5E7EB ${((inputs[(question as SliderQuestion).field] as number) - ((question as SliderQuestion).min || 0)) / (((question as SliderQuestion).max || 100) - ((question as SliderQuestion).min || 0)) * 100}%, #E5E7EB 100%)`
            }} data-id="element-630" />
                  <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-631">
                    <span data-id="element-632">
                      {(question as SliderQuestion).formatValue ? (question as SliderQuestion).formatValue!((question as SliderQuestion).min || 0) : (question as SliderQuestion).min}
                    </span>
                    <span data-id="element-633">
                      {(question as SliderQuestion).formatValue ? (question as SliderQuestion).formatValue!((question as SliderQuestion).max || 100) : (question as SliderQuestion).max}
                    </span>
                  </div>
                </div>}
            </div>)}
        </div>

        {/* CTA */}
        <div className="mt-8 text-center" data-id="element-634">
          <button onClick={handleContinue} className="inline-flex items-center gap-3 px-8 py-4 bg-amber hover:bg-amber-dark text-navy font-bold text-lg rounded-lg transition-all shadow-xl hover:shadow-amber/50 hover:scale-105 active:scale-100" data-id="element-635">
            <span data-id="element-636">Continue to Recruiting Funnel</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-637">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" data-id="element-638" />
            </svg>
          </button>
        </div>
      </div>
    </div>;
}